import { useState, useEffect, useRef } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import axios from 'axios';
import RichTextEditor from '@/components/ui/rich-text-editor';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Search, 
  Calendar,
  User,
  FileText,
  Upload,
  Loader,
  Heart,
  Share2,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editorKey, setEditorKey] = useState(Date.now()); // Forzar recreación de editores
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    image_url: '',
    author: '',
    is_published: true
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('No estás autenticado. Por favor inicia sesión.');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API}/blog/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data || []);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Error al cargar los artículos';
      toast.error(errorMsg);
      console.error('Error fetching posts:', error);
      
      // Si el error es de autenticación, redirigir al login
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Sesión expirada. Por favor inicia sesión nuevamente.');
        setTimeout(() => {
          window.location.href = '/gestion-agencia/login';
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Auto-generate slug from title
    if (name === 'title' && !editingPost) {
      const slug = value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove accents
        .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Remove duplicate hyphens
        .trim();
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar que hay imagen
    if (!formData.image_url) {
      toast.error('Por favor selecciona o genera una imagen para el artículo');
      return;
    }
    
    const token = localStorage.getItem('token');

    try {
      if (editingPost) {
        // Update existing post
        await axios.put(
          `${API}/blog/${editingPost.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Artículo actualizado correctamente');
      } else {
        // Create new post
        await axios.post(
          `${API}/blog`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Artículo creado correctamente');
      }

      fetchPosts();
      closeModal();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al guardar el artículo';
      toast.error(errorMsg);
      console.error('Error saving post:', error);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este artículo?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/blog/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Artículo eliminado correctamente');
      fetchPosts();
    } catch (error) {
      toast.error('Error al eliminar el artículo');
      console.error('Error deleting post:', error);
    }
  };

  const handleTogglePublish = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API}/blog/${postId}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchPosts();
    } catch (error) {
      toast.error('Error al cambiar el estado de publicación');
      console.error('Error toggling publish:', error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona un archivo de imagen');
      return;
    }

    // Validate file size (max 15MB)
    if (file.size > 15 * 1024 * 1024) {
      toast.error('La imagen es demasiado grande. Máximo 15MB');
      return;
    }

    setUploadingImage(true);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'blog');

      const response = await axios.post(`${API}/media/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('Imagen subida correctamente');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Error al subir la imagen');
    } finally {
      setUploadingImage(false);
    }
  };

  const openModal = (post = null) => {
    // Regenerar key para forzar recreación de editores
    setEditorKey(Date.now());
    
    if (post) {
      setEditingPost(post);
      setFormData({
        title: post.title,
        slug: post.slug,
        excerpt: post.excerpt,
        content: post.content,
        image_url: post.image_url,
        author: post.author,
        is_published: post.is_published
      });
    } else {
      setEditingPost(null);
      setFormData({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        image_url: '',
        author: '',
        is_published: true
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPost(null);
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      image_url: '',
      author: '',
      is_published: true
    });
  };

  const filteredPosts = posts.filter(post =>
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-serif font-bold text-primary">Gestión del Blog</h1>
            <p className="text-muted-foreground mt-1">
              Crea y gestiona los artículos de tu blog
            </p>
          </div>
          <button
            onClick={() => openModal()}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Nuevo Artículo
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar artículos por título, autor o contenido..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Artículos</p>
                <p className="text-2xl font-bold text-primary">{posts.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <Eye className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Publicados</p>
                <p className="text-2xl font-bold text-primary">
                  {posts.filter(p => p.is_published).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <EyeOff className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Borradores</p>
                <p className="text-2xl font-bold text-primary">
                  {posts.filter(p => !p.is_published).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Posts List */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          {filteredPosts.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {searchTerm ? 'No se encontraron artículos' : 'No hay artículos creados'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Artículo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Autor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Estadísticas
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredPosts.map((post) => (
                    <tr key={post.id} className="hover:bg-slate-50 transition">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {post.image_url && (
                            <img
                              src={post.image_url}
                              alt={post.title}
                              className="w-16 h-16 object-cover rounded flex-shrink-0"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/64x64?text=No+Image';
                              }}
                            />
                          )}
                          <div>
                            <p className="font-medium text-slate-900">{post.title}</p>
                            <p className="text-sm text-muted-foreground line-clamp-1">
                              {post.excerpt.replace(/<[^>]*>/g, '').substring(0, 100)}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              /{post.slug}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <User className="w-4 h-4" />
                          {post.author}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Calendar className="w-4 h-4" />
                          {new Date(post.created_at).toLocaleDateString('es-ES')}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center gap-2 text-xs text-red-600 font-medium">
                            <Heart className="w-3.5 h-3.5 fill-current" />
                            <span>{post.likes || 0}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-blue-600 font-medium">
                            <MessageSquare className="w-3.5 h-3.5" />
                            <span>{post.comment_count || 0}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-green-600 font-medium">
                            <Share2 className="w-3.5 h-3.5" />
                            <span>{post.shares || 0}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            post.is_published
                              ? 'bg-green-100 text-green-800'
                              : 'bg-orange-100 text-orange-800'
                          }`}
                        >
                          {post.is_published ? (
                            <>
                              <Eye className="w-3 h-3" />
                              Publicado
                            </>
                          ) : (
                            <>
                              <EyeOff className="w-3 h-3" />
                              Borrador
                            </>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleTogglePublish(post.id)}
                            className="p-2 hover:bg-slate-100 rounded-lg transition"
                            title={post.is_published ? 'Despublicar' : 'Publicar'}
                          >
                            {post.is_published ? (
                              <EyeOff className="w-4 h-4 text-orange-600" />
                            ) : (
                              <Eye className="w-4 h-4 text-green-600" />
                            )}
                          </button>
                          <button
                            onClick={() => openModal(post)}
                            className="p-2 hover:bg-slate-100 rounded-lg transition"
                            title="Editar"
                          >
                            <Edit className="w-4 h-4 text-blue-600" />
                          </button>
                          <button
                            onClick={() => handleDelete(post.id)}
                            className="p-2 hover:bg-slate-100 rounded-lg transition"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal for Create/Edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4">
              <h2 className="text-2xl font-serif font-bold text-primary">
                {editingPost ? 'Editar Artículo' : 'Nuevo Artículo'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Título *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className="input w-full"
                  placeholder="El título de tu artículo"
                />
              </div>

              {/* Slug */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Slug (URL) *
                </label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  required
                  className="input w-full font-mono text-sm"
                  placeholder="titulo-del-articulo"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  URL: /blog/{formData.slug || 'slug'}
                </p>
              </div>

              {/* Author */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Autor *
                </label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                  required
                  className="input w-full"
                  placeholder="Nombre del autor"
                />
              </div>

              {/* Image Selection */}
              <div className="space-y-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Imagen del artículo *
                </label>
                
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="btn-secondary w-full"
                  >
                    {uploadingImage ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin mr-2" /> 
                        Subiendo...
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5 mr-2" /> 
                        Seleccionar imagen
                      </>
                    )}
                  </button>
                  <p className="text-xs text-muted-foreground mt-1">
                    Tamaño máximo: 15MB. Formatos: JPG, PNG, WebP
                  </p>
                </div>
                
                {/* Image Preview */}
                {formData.image_url && (
                  <div className="relative mt-4">
                    <img
                      src={formData.image_url}
                      alt="Preview"
                      crossOrigin="anonymous"
                      className="w-full h-64 object-cover rounded-lg border border-slate-200"
                      style={{ maxHeight: '256px' }}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/1200x630?text=Error+al+cargar+imagen';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                      className="absolute top-2 right-2 p-2 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg transition"
                      title="Eliminar imagen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Excerpt */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Extracto *
                </label>
                <RichTextEditor
                  key={`excerpt-${editorKey}`}
                  id={`excerpt-${editorKey}`}
                  value={formData.excerpt}
                  onChange={(value) => setFormData(prev => ({...prev, excerpt: value}))}
                  placeholder="Breve resumen del artículo (se mostrará en las tarjetas)..."
                  height="120px"
                  simple={true}
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Contenido *
                </label>
                <RichTextEditor
                  key={`content-${editorKey}`}
                  id={`content-${editorKey}`}
                  value={formData.content}
                  onChange={(value) => setFormData(prev => ({...prev, content: value}))}
                  placeholder="El contenido completo del artículo..."
                  height="400px"
                  simple={false}
                />
              </div>

              {/* Published Status */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_published"
                  name="is_published"
                  checked={formData.is_published}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-primary focus:ring-primary border-slate-300 rounded"
                />
                <label htmlFor="is_published" className="text-sm font-medium text-slate-700">
                  Publicar artículo (visible para los usuarios)
                </label>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  {editingPost ? 'Actualizar' : 'Crear'} Artículo
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
